export type Lang = "sv" | "en";

export const translations = {
  sv: {
    nav: {
      topBanner: "Mottagning i Göteborg · Digitalt i hela Sverige",
      certificates: "Läkarintyg",
      checkups: "Hälsokontroller",
      drugTesting: "Drogtestning",
      faq: "Frågor & svar",
      visit: "Inför besöket",
      contact: "Kontakt",
      private: "Privat",
      company: "Företag",
      book: "Boka tid",
      searchOpen: "Sök läkarintyg",
      searchClose: "Stäng sök",
      searchPlaceholder: "Sök efter intyg, t.ex. körkort eller dykning…",
      searchNoResults: "Inga intyg matchade din sökning.",
    },
    hero: {
      eyebrow: "Läkarintyg för privatpersoner & företag",
      title: "Läkarintyg — utan krångel.",
      titlePrefix: "Läkarintyg för",
      subtitle:
        "Intygsläkaren är en mottagning på Järntorget i Göteborg — och en digital klinik för privatpersoner och företag i hela Sverige. Vi hjälper dig med läkarintyg och medicinska utredningar: från körkort och sjöfolk till resor, försäkring och företagshälsa. Boka tid online, få tydligt pris från start och träffa erfarna läkare som tar dig på allvar.",
      bookCta: "Boka tid idag",
      browseCta: "Se alla läkarintyg",
      searchPlaceholder: "Sök efter intyg, t.ex. körkort eller dykning…",
      searchAriaLabel: "Sök bland läkarintyg",
      searchCta: "Sök",
      searchNoResults: "Inga intyg matchade din sökning.",
      popularLabel: "Mest efterfrågat",
      googleReviewsLabel: "Baserat på Google-recensioner",
      stat1: "30+",
      stat1Label: "typer av intyg",
      stat2: "4.9/5",
      stat2Label: "Kundbetyg",
      stat3: "Samma vecka",
      stat3Label: "lediga tider",
      metaTitle: "Intygsläkaren – Digitala Läkarintyg snabbt och professionellt",
      metaDescription:
        "Intygsläkaren utfärdar digitala läkarintyg för körkort, sjöfolk, arbete, resor, försäkring och mer. Boka tid online – snabbt, smidigt och professionellt.",
    },
    trust: {
      title: "Läkarintyg inom flera områden",
      subtitle: "Vi erbjuder läkarintyg inom flera områden — digitalt eller på mottagningen i Göteborg.",
      areas: {
        korkort: "Högre behörighet, diabetes, ADHD, hjärta och övriga körkortstillstånd.",
        alkohol: "Alkolås, alkohol- och drogintyg samt privat drogtestning.",
        sjofolk: "Sjöfolksintyg, offshore och fartygshälsokontroller.",
        arbete: "Au pair, förstadagsintyg, utlandsstudier och nattarbete.",
        resor: "Fit-to-fly, reseintyg och intyg vid graviditet.",
        forsakring: "Försäkringsintyg och invaliditetsintyg.",
        sport: "Fallskärm, tävling och andra idrottsintyg.",
        adoption: "Hälsointyg inför adoption.",
        afs: "Arbetsmiljöundersökningar enligt AFS.",
        ovriga: "Andra läkarintyg — kontakta oss så hjälper vi dig.",
      },
    },
    services: {
      eyebrow: "Våra tjänster",
      title: "Hitta rätt läkarintyg",
      allTitle: "Alla våra läkarintyg",
      subtitle: "Över 30 typer av läkarintyg för körkort, sjöfolk, arbete, resor, försäkring, sport och mer.",
      searchPlaceholder: "Sök efter intyg, t.ex. körkort eller dykning…",
      searchAriaLabel: "Sök bland läkarintyg",
      noResults: "Inga intyg matchade din sökning. Prova ett annat ord eller kontakta oss.",
      filterAll: "Alla",
      bookCta: "Boka",
      readMore: "Läs mer",
      readMoreAbout: "Läs mer om {title}",
      onsite: "På plats",
      digital: "Digitalt",
      empty: "Hittar du inte det intyg du söker? Kontakta oss så hjälper vi dig.",
    },
    categories: {
      korkort: "Körkort",
      alkohol: "Alkohol & droger",
      sjofolk: "Sjöfolk",
      arbete: "Arbete & studier",
      resor: "Resor",
      forsakring: "Försäkring",
      sport: "Sport & aktiviteter",
      adoption: "Adoption",
      afs: "Arbetsmiljö (AFS)",
      ovriga: "Övriga intyg",
    },
    startCase: {
      eyebrow: "Välj intyg",
      title: "Hitta rätt läkarintyg",
      subtitle: "Välj område och intygstyp — digitalt eller på mottagningen i Göteborg.",
      back: "Alla områden",
      pickArea: "Vad gäller ditt ärende?",
      pickAreaHint: "Välj det område som stämmer bäst. Du lämnar inga medicinska uppgifter i detta steg.",
      pickCertificate: "Vilket intyg behöver du?",
      pickCertificateHint: "Välj det alternativ som stämmer bäst. Du lämnar inga medicinska uppgifter i detta steg.",
      forPrefix: "Intyg för",
      browseAllTitle: "Vill du se hela utbudet?",
      browseAllText: "Alla intyg finns samlade kategori för kategori.",
      browseAllCta: "Visa alla läkarintyg",
      priceLabel: "Pris",
    },
    business: {
      eyebrow: "För företag",
      title: "Företagshälsovård som följer er takt",
      text: "Vi hjälper företag, rederier och organisationer med arbetsmiljöintyg, nattarbete, AFS-undersökningar och offshore-hälsokontroller. Skräddarsydda upplägg, fakturering och löpande samarbete.",
      cta: "Koppla ditt företag till oss",
    },
    reviews: {
      eyebrow: "Patientomdömen",
      title: "Vad våra patienter säger",
      subtitle: "Sammanställt från verifierade Google-recensioner.",
    },
    visit: {
      eyebrow: "Inför besöket",
      title: "Så går besöket till",
      steps: [
        { title: "1. Boka tid", text: "Välj intyg och boka online — många intyg går att utföra digitalt." },
        {
          title: "2. Träffa läkaren",
          text: "Du tas emot direkt på utsatt tid. Vi går igenom intyget och eventuella prover.",
        },
        { title: "3. Få ditt intyg", text: "De flesta intyg utfärdas direkt eller skickas inom 1–2 arbetsdagar." },
      ],
    },
    cta: {
      title: "Redo att boka tid?",
      subtitle: "Lediga tider redan i veckan. Boka online på under en minut.",
      primary: "Boka tid",
      secondary: "Ring 031-761 77 00",
    },
    footer: {
      tagline: "Läkarintyg & medicinska utredningar i centrala Göteborg för privatpersoner och företag, på plats och digitalt.",
      navTitle: "Navigering",
      contactTitle: "Kontakt",
      legalTitle: "Information",
      address: "Järntorgsgatan 8, 413 30 Göteborg",
      cookies: "Cookie-policy",
      privacy: "Integritetspolicy",
      terms: "Användarvillkor",
      manageCookies: "Hantera cookies",
      rights: "Alla rättigheter förbehållna.",
      createdBy: "Hemsidan skapad av",
    },
    cookies: {
      text: "Vi använder cookies för att förbättra din upplevelse. Genom att fortsätta kan du välja vilka cookies du godkänner.",
      policyLink: "Läs cookiepolicyn",
      accept: "Acceptera",
      decline: "Avvisa",
      settings: "Inställningar",
      modalTitle: "Cookie-inställningar",
      modalDescription:
        "Välj vilka typer av cookies du vill acceptera. Nödvändiga cookies kan inte inaktiveras eftersom de är nödvändiga för att webbplatsen ska fungera.",
      necessaryTitle: "Nödvändiga cookies",
      necessaryText: "Dessa cookies är nödvändiga för att webbplatsen ska fungera korrekt.",
      analyticsTitle: "Analytiska cookies",
      analyticsText:
        "Aktiverar Google Analytics 4 via Google Tag Manager så att vi kan förstå hur webbplatsen används.",
      marketingTitle: "Marknadsföringscookies",
      marketingText:
        "Aktiverar annons- och konverteringspixlar (t.ex. Meta Pixel) via Google Tag Manager för kampanjmätning och relevant annonsering.",
      acceptAll: "Acceptera alla",
      saveSettings: "Spara inställningar",
    },
    contact: {
      eyebrow: "Kontakt",
      title: "Kontakta oss",
      subtitle: "Vi hjälper dig med snabba och pålitliga läkarintyg. Kontakta oss för bokning eller frågor.",
      bookCta: "Boka tid",
      callCta: "Ring oss",
      directionsCta: "Hitta hit",
      formTitle: "Skicka oss ett meddelande",
      formSubtitle: "Har du frågor eller vill boka en tid? Fyll i formuläret så återkommer vi så snart som möjligt.",
      name: "Namn",
      email: "E-post",
      company: "Företagsnamn",
      phone: "Telefonnummer",
      message: "Meddelande",
      attachment: "Bifoga filer (max 3 filer)",
      attachmentHint: "Tillåtna filer: PNG, JPG/JPEG, WEBP eller PDF. Max 3 filer.",
      attachmentInvalid: "Ogiltig filtyp. Ladda upp PNG, JPG/JPEG, WEBP eller PDF.",
      attachmentTooMany: "Du kan ladda upp max 3 filer.",
      consent: "Jag godkänner att mina personuppgifter behandlas enligt integritetspolicyn.",
      send: "Skicka meddelande",
      sent: "Tack! Vi återkommer så snart som möjligt.",
      infoTitle: "Kontaktinformation",
      callDirect: "Ring direkt",
      sendEmail: "Skicka e-post",
      visitUs: "Besök oss",
      hours: "Öppettider",
      hoursWeek: "Måndag–Fredag: 08:00–17:00",
      hoursSat: "Lördag: Stängt",
      hoursSun: "Söndag: Stängt",
      address: "Järntorgsgatan 8, 413 30 Göteborg",
      mapTitle: "Hitta hit",
      mapSubtitle: "Vi ligger centralt i Göteborg med god tillgång till kollektivtrafik.",
      faqTitle: "Vanliga frågor",
      faqTimeQ: "Hur lång tid tar det att få ett intyg?",
      faqTimeA: "De flesta intyg kan utfärdas samma dag eller inom 48 timmar efter undersökningen.",
      faqBookingQ: "Behöver jag boka tid i förväg?",
      faqBookingA: "Ja, vi rekommenderar att du bokar tid för att säkerställa att vi kan hjälpa dig när det passar dig.",
      faqPaymentQ: "Vilka betalningsmetoder accepterar ni?",
      faqPaymentA: "Vi accepterar kortbetalning, Klarna och Swish.",
    },
    booking: {
      eyebrow: "Boka tid",
      title: "Boka tid för läkarintyg",
      subtitle:
        "Boka en tid för läkarintyg eller medicinsk konsultation. Välj ett datum och tid som passar dig bäst.",
      callCta: "Ring oss",
      contactCta: "Kontakta oss",
      sectionTitle: "Boka din tid online",
      sectionSubtitle:
        "Välj ett datum och tid som passar dig bäst. Du kan boka tid för alla våra tjänster direkt online eller ringa oss för personlig hjälp.",
      helpTitle: "Behöver du hjälp med bokningen?",
      helpText: "Har du frågor om bokningen eller behöver hjälp? Kontakta oss gärna!",
      iframeTitle: "Boka tid - Intygsläkaren",
    },
    faq: {
      eyebrow: "Frågor & svar",
      title: "Frågor och svar",
      subtitle:
        "Här hittar du svar på vanliga frågor om läkarintyg, bokning, kostnader och procedurer.",
      bookCta: "Boka tid",
      contactCta: "Kontakta oss",
      stillTitle: "Har du ytterligare frågor?",
      stillText:
        "Tveka inte att kontakta oss via telefon eller e-post, så hjälper vi dig gärna!",
      sections: [
        {
          title: "Allmänna frågor",
          items: [
            {
              q: "Vad är Intygsläkaren?",
              a: "Intygsläkaren är en specialistmottagning i centrala Göteborg som utfärdar läkarintyg och utför medicinska utredningar för privatpersoner och företag. Vi erbjuder över 30 typer av intyg med snabba tider och tydliga priser.",
            },
            {
              q: "Hur bokar jag en tid hos Intygsläkaren?",
              a: "Du bokar enklast online via vår bokningssida. Du kan också ringa oss på 031-761 77 00 eller skicka e-post till info@intygslakaren.se så hjälper vi dig.",
            },
            {
              q: "Vad ska jag ta med mig till mitt besök?",
              a: "Ta alltid med giltig legitimation. Beroende på vilket intyg du ska ha kan vi behöva tidigare medicinska handlingar, glasögon/linser, eventuella formulär från arbetsgivare/myndighet samt information om mediciner du tar.",
            },
          ],
        },
        {
          title: "Specifika intyg",
          items: [
            {
              q: "Vad är ett körkortsintyg och varför behöver jag det?",
              a: "Ett körkortsintyg är ett medicinskt intyg som krävs av Transportstyrelsen vid ansökan eller förnyelse av vissa körkortsbehörigheter (t.ex. tung lastbil, buss, taxi) eller efter sjukdom. Vi utfärdar samtliga körkortsintyg och skickar in dem digitalt direkt.",
            },
            {
              q: "Behöver jag ett läkarintyg för att studera eller arbeta utomlands?",
              a: "Många universitet, arbetsgivare och visumprogram kräver intyg om hälsa, vaccinationer eller specifika undersökningar. Kontakta oss med kraven så hjälper vi dig att utfärda rätt intyg på svenska eller engelska.",
            },
            {
              q: "Vad innebär ett fallskärmsintyg?",
              a: "Ett fallskärmsintyg är en hälsoundersökning enligt Svenska Fallskärmsförbundets krav. Vi går igenom hjärt-kärl, syn, hörsel och allmäntillstånd och utfärdar intyget efter besöket.",
            },
          ],
        },
        {
          title: "Procedurer och kostnader",
          items: [
            {
              q: "Hur går en medicinsk undersökning till hos Intygsläkaren?",
              a: "Du tas emot direkt på utsatt tid. Läkaren går igenom din hälsohistoria, gör nödvändiga undersökningar (t.ex. syn, hörsel, blodtryck) och fyller i intyget. De flesta intyg utfärdas direkt vid besöket.",
            },
            {
              q: "Hur mycket kostar ett medicinskt intyg?",
              a: "Vi har fasta och tydliga priser per intygstyp. Aktuella priser hittar du på respektive intygssida — inga dolda kostnader.",
            },
            {
              q: "Kan jag avboka min tid?",
              a: "Ja, du kan avboka kostnadsfritt fram till 24 timmar före din bokade tid. Avbokningar senare än så debiteras enligt prislistan.",
            },
          ],
        },
        {
          title: "Övriga frågor",
          items: [
            {
              q: "Hur snabbt får jag mitt intyg?",
              a: "De flesta intyg utfärdas direkt vid besöket eller skickas digitalt inom 1–2 arbetsdagar. Vissa intyg som kräver provsvar kan ta något längre.",
            },
            {
              q: "Kan ni hjälpa till med att fylla i specifika formulär från myndigheter eller organisationer?",
              a: "Ja, vi fyller i formulär från Transportstyrelsen, Försäkringskassan, försäkringsbolag, arbetsgivare, idrottsförbund och utländska myndigheter. Ta med formuläret till besöket.",
            },
          ],
        },
      ],
    },
    visitPage: {
      eyebrow: "Inför besöket",
      title: "Info om besöket",
      subtitle:
        "Boka videomöte eller besök vår klinik i Göteborg. Vi erbjuder flexibla alternativ för alla typer av medicinska intyg.",
      bookCta: "Boka tid",
      contactCta: "Kontakta oss",
      optionsTitle: "Välj ditt besöksalternativ",
      optionsSubtitle:
        "Vi erbjuder både videomöten och fysiska besök på vår klinik. Vissa intyg kräver fysiskt besök medan andra kan utföras online.",
      videoTitle: "Videomöte",
      videoText:
        "Boka ett videomöte för bekvämt och snabbt läkarintyg från hemmet eller kontoret.",
      videoBullets: [
        "Bekvämt från hemmet eller kontoret",
        "Snabb bokning och genomförande",
        "Samma professionella service",
        "Intyg skickas digitalt",
      ],
      clinicTitle: "Klinikbesök",
      clinicText:
        "Besök vår klinik i centrala Göteborg för komplett medicinsk undersökning och intygsutskrivning.",
      clinicBullets: [
        "Komplett medicinsk undersökning",
        "Syn- och hörseltest",
        "Blodprov och fysiska tester",
        "Personlig service på plats",
      ],
      clinicAddress: "Järntorgsgatan 8, Göteborg",
      bringTitle: "Vad ska jag ta med mig?",
      bringItems: [
        "Giltig legitimation (ID-kort eller pass)",
        "Glasögon eller kontaktlinser om du använder dem",
        "Specifika intygskrav från den organisation som kräver intyget",
        "Medicinlista om du använder läkemedel",
        "För videomöte: god internetanslutning och en fungerande kamera",
      ],
      duringTitle: "Vad händer under besöket?",
      durationTitle: "Varaktighet",
      durationText:
        "Ett vanligt besök tar cirka 30–60 minuter beroende på typen av intyg och undersökning.",
      examTitle: "Undersökning",
      examText:
        "Läkaren genomför en medicinsk bedömning och de tester som krävs för ditt specifika intyg.",
      issueTitle: "Intygsutskrivning",
      issueText:
        "Om du uppfyller kraven utfärdas intyget direkt efter avslutad undersökning. Du får det med dig eller skickat digitalt.",
      readyTitle: "Redo att boka ditt besök?",
      readyText: "Välj mellan videomöte eller klinikbesök och boka din tid idag.",
    },
    companies: {
      eyebrow: "Intygsläkaren för företag",
      title: "Läkarintyg och provtagning för era medarbetare.",
      subtitle:
        "Boka ett läkarintyg eller en medicinsk kontroll för en medarbetare. Behöver ni hjälp med flera personer, drogtestning eller ett återkommande upplägg? Skicka en företagsförfrågan så planerar vi tillsammans.",
      bookCta: "Boka en medarbetare",
      inquiryCta: "Skicka företagsförfrågan",
      heroImageAlt: "Läkare i möte med företagskunder",
      heroCaption: "Mottagning i Göteborg",
      heroCaptionStrong: "Personlig läkarkontakt för ert företag",
      entriesAria: "Välj företagstjänst",
      entries: [
        {
          title: "Medicinska kontroller",
          text: "Handintensivt arbete, vibrationer, nattarbete och övriga arbetsmiljökontroller.",
          linkLabel: "Arbetsmiljö",
          hrefKey: "arbetsmiljo" as const,
        },
        {
          title: "Drogtestning för företag",
          text: "Förebyggande, vid nyanställning och vid misstanke. Alla drogprover granskas av MRO-läkare.",
          linkLabel: "Drogtestning",
          hrefKey: "drogtestning" as const,
        },
        {
          title: "Alla våra läkarintyg",
          text: "Samma tjänster samlade för er som arbetsgivare. Välj intyg eller be oss planera flera tillsammans.",
          linkLabel: "Alla tjänster",
          hrefKey: "tjanster" as const,
        },
      ],
      assurance: [
        {
          title: "Alla drogprover granskas av MRO-läkare",
          text: "Medicinsk granskning av provresultatet.",
        },
        {
          title: "Laboratorieanalys via Unilabs",
          text: "Vår samarbetspartner för drogprover.",
        },
      ],
      processEyebrow: "Från förfrågan till genomförande",
      processTitle: "En gemensam plan för ert företag",
      steps: [
        {
          number: "01",
          title: "Berätta vad ni behöver",
          text: "Välj tjänst och ange antal personer, arbetsplats och önskad period.",
        },
        {
          number: "02",
          title: "Vi planerar tillsammans",
          text: "Vi går igenom underlag, besök, provtagning och pris innan ni bekräftar upplägget.",
        },
        {
          number: "03",
          title: "Besök och återkoppling",
          text: "Medarbetarna får instruktioner. Medicinska uppgifter hanteras inom vården och återkopplingen planeras med er.",
        },
      ],
      ctaEyebrow: "Kontakt för arbetsgivare",
      ctaTitle: "Vi planerar utifrån ert behov",
      ctaText:
        "Berätta vilka tjänster ni behöver, för hur många personer och när. Vi återkommer med upplägg, pris och tider.",
      formTitle: "Företagsförfrågan",
      formSubtitle:
        "Berätta vad ni behöver hjälp med — intyg, medicinska kontroller eller drogtestning. Vi återkommer via e-post med upplägg, pris och tider.",
      formPrivacy:
        "Ange inte medicinska uppgifter om enskilda medarbetare i formuläret. Sådant hanteras inom vården vid besöket.",
      peopleCount: "Antal personer",
      workplace: "Arbetsplats / ort",
      period: "Önskad period",
      metaTitle: "Läkarintyg och drogtestning för företag i Göteborg | Intygsläkaren",
      metaDescription:
        "Läkarintyg, medicinska kontroller och drogtestning för företag i Göteborg. MRO-granskning, Unilabs och personlig läkarkontakt.",
    },
    about: {
      eyebrow: "Om oss",
      title: "Om Intygsläkaren",
      subtitle:
        "Vi är en specialistmottagning i centrala Göteborg som utfärdar läkarintyg och utför medicinska utredningar för privatpersoner och företag.",
      introTitle: "Vår mottagning",
      introText1:
        "Intygsläkaren startades med målet att göra läkarintyg enkelt, snabbt och tryggt. Vi är specialiserade på medicinska utredningar och intyg och tar emot patienter på Järntorget i Göteborg samt digitalt via videomöte.",
      introText2:
        "Vårt team består av erfarna specialistläkare med lång erfarenhet av intygsskrivning. Vi arbetar med över 30 typer av intyg — från körkort och sjöfolk till resor, försäkring, sport och företagshälsa.",
      valuesTitle: "Det här står vi för",
      values: [
        { title: "Trygghet & kvalitet", text: "Vi följer Sveriges högsta sekretess- och kvalitetskrav i varje besök." },
        { title: "Tydliga priser", text: "Fasta priser per intygstyp — inga dolda kostnader eller överraskningar." },
        { title: "Snabb service", text: "Lediga tider redan samma vecka och många intyg utfärdas direkt." },
        { title: "Personligt bemötande", text: "Vi tar oss tid och möter dig professionellt, lyhört och respektfullt." },
      ],
      ctaTitle: "Välkommen till oss",
      ctaText: "Boka tid online eller hör av dig så hjälper vi dig vidare.",
      ctaPrimary: "Boka tid",
      ctaSecondary: "Kontakta oss",
    },
    healthChecks: {
      eyebrow: "Hälsokontroller",
      title: "Hälsokontroller",
      subtitle:
        "Intygsläkaren samarbetar med Göteborgskliniken för hälsokontroller. Se alla tillgängliga hälsokontroller och boka via Göteborgskliniken.",
      partnerTitle: "Vårt samarbete",
      partnerText1:
        "Intygsläkaren samarbetar med Göteborgskliniken för att erbjuda omfattande hälsokontroller. Göteborgskliniken är specialiserade på preventivmedicin och noggrant utformade hälsokontroller som ger dig en skräddarsydd överblick av din hälsa.",
      partnerText2:
        "Alla hälsokontroller bokas och genomförs via Göteborgskliniken. Du kan enkelt boka din tid direkt på deras webbplats.",
      bookAtPartner: "Boka hos Göteborgskliniken",
      bookCta: "Boka",
      popular: "Mest populär",
      sections: [
        {
          title: "Populära kontroller",
          items: [
            { name: "Basala Hälsokollen", price: "995 kr", desc: "Din hälsa i fokus – en enkel och effektiv hälsokoll för alla.", points: ["31 hälsomarkörer", "Blodtryck, puls, längd och vikt", "Kolesterol", "Upptäck blodbrist", "Upptäck riskfaktorer"], href: "https://goteborgskliniken.se/halsokontroller/basala-halsokollen/" },
            { name: "Stora Hälsokollen", price: "1695 kr", desc: "Din hälsa i fokus – en större och effektiv hälsokoll för alla.", points: ["49 hälsomarkörer", "Blodtryck, puls, längd och vikt", "Hormoner", "Vitaminer", "PSA för män över 50"], href: "https://goteborgskliniken.se/halsokontroller/stora-halsokollen/", popular: true },
            { name: "Elit Hälsokollen", price: "2495 kr", desc: "Din hälsa i fokus – en komplett och avancerad hälsokoll för alla.", points: ["57 hälsomarkörer", "Blodtryck, puls, längd och vikt", "Avancerade tester", "Maximal insikt", "Upptäck sjukdomar i förtid"], href: "https://goteborgskliniken.se/halsokontroller/elit-halsokollen/" },
          ],
        },
        {
          title: "Organspecifika",
          items: [
            { name: "Hjärt- och kärlhälsan", price: "", desc: "För ett starkt hjärta och friska kärl – ta kontroll över din hjärt- och kärlhälsa.", points: ["Kolesterolprofiler: HDL och LDL", "Riskkvoter: HDL/LDL", "Apolipoproteiner: ApoA1, ApoB", "Åtta viktiga markörer"], href: "https://goteborgskliniken.se/halsokontroller/hjart-och-karlhalsan/" },
            { name: "Diabeteshälsan", price: "", desc: "För stabilt blodsocker och en hälsosam framtid – ta kontroll över din diabeteshälsa.", points: ["Bukspottskörtelfunktion", "Glukos", "HbA1c", "C-peptid", "Tre hälsomarkörer"], href: "https://goteborgskliniken.se/halsokontroller/diabeteshalsan/" },
            { name: "Prostatahälsan", price: "", desc: "PSA-screening för tidig upptäckt av prostataförändringar.", points: ["PSA-screening för tidig upptäckt", "Rekommenderad: 50–75 år", "Årlig kontroll upptäcker tidiga förändringar"], href: "https://goteborgskliniken.se/halsokontroller/prostatahalsan/" },
            { name: "Njurhälsan", price: "", desc: "För friska njurar och en balanserad kropp – ta kontroll över din njurhälsa.", points: ["Mätning av Cystatin-C", "Mätning av njurfunktion", "Djup elektrolytgenomgång", "Klorid och fosfat"], href: "https://goteborgskliniken.se/halsokontroller/njurhalsan/" },
            { name: "Blodhälsan", price: "", desc: "För en stark kropp och ett robust immunförsvar – ta kontroll över din blodhälsa.", points: ["Fullständig blodstatus", "Genomgång av immunförsvar", "Upptäck blodsjukdomar"], href: "https://goteborgskliniken.se/halsokontroller/blodhalsan/" },
            { name: "Leverhälsan", price: "", desc: "För en frisk lever och optimal hälsa – ta kontroll över din leverfunktion.", points: ["Genomgång - 5 hälsomarkörer", "Upptäck gulsot", "Upptäck leversjukdomar"], href: "https://goteborgskliniken.se/halsokontroller/leverhalsan/" },
          ],
        },
        {
          title: "Hormoner",
          items: [
            { name: "Testosteron", price: "", desc: "För optimal energi, styrka och välmående – övervaka dina testosteronnivåer.", points: ["Enkelt testosteronvärde", "Passar dig som vill upptäcka brist"], href: "https://goteborgskliniken.se/halsokontroller/testosteron/" },
            { name: "Fördjupande testosteron", price: "", desc: "För optimal energi, styrka och välmående – övervaka och balansera dina testosteronnivåer.", points: ["Testosteronmätning", "Genomgång av SHBG", "Bioaktivt - 'fritt' testosteron", "LH-nivåer", "Komplett blodstatus"], href: "https://goteborgskliniken.se/halsokontroller/fordjupande-testosteron/" },
            { name: "Fördjupande sköldkörtel", price: "", desc: "För en balanserad ämnesomsättning och optimal hälsa – övervaka din sköldkörtelfunktion.", points: ["Kontroll av sköldkörteln", "Antikroppar - TPO och TRAK", "Fritt T4 och T3", "Mätning av TSH"], href: "https://goteborgskliniken.se/halsokontroller/fordjupande-skoldkortel/" },
            { name: "Östrogen", price: "", desc: "För hormonell balans och välbefinnande – övervaka dina östrogennivåer.", points: ["Mäter östrogennivåer effektivt", "Insikt i östrogenproduktionen", "Identifierar orsaker till symtom"], href: "https://goteborgskliniken.se/halsokontroller/ostrogen/" },
            { name: "Progesteron", price: "", desc: "För hormonell balans och optimal hälsa – övervaka dina progesteronnivåer.", points: ["Mäter progesteronnivåer", "Menstruationscykelns hälsa", "Identifierar fertilitetsrelaterade problem"], href: "https://goteborgskliniken.se/halsokontroller/progesteron/" },
            { name: "FSH och Östrogen", price: "", desc: "För en balanserad hormonell hälsa och välbefinnande – övervaka dina FSH- och östrogennivåer.", points: ["Mäter FSH och östrogen nivåer", "Bedömer fertilitet och ägglossning", "Ger insikt i hormonbalansen"], href: "https://goteborgskliniken.se/halsokontroller/fsh-och-ostrogen/" },
          ],
        },
        {
          title: "Allergi",
          items: [
            { name: "Trädpollenallergi", price: "", desc: "Lindra dina allergisymptom och förbättra din livskvalitet – ta kontroll över din pollenallergi.", points: ["Identifierar allergi mot trädpollen", "Bekräftar specifika trädallergener", "Underlättar anpassad behandlingsplan", "Förbättrar symtomhantering under pollensäsong"], href: "https://goteborgskliniken.se/halsokontroller/tradpollenallergi/" },
            { name: "Glutenintoleranstest", price: "", desc: "Upptäck glutenintolerans och förbättra din matsmältning.", points: ["Identifierar glutenintolerans", "Bekräftar celiaki", "Underlättar anpassad kostplan"], href: "https://goteborgskliniken.se/halsokontroller/glutenintoleranstest/" },
          ],
        },
        {
          title: "Vitaminer",
          items: [
            { name: "B12 (Kobalamin)", price: "", desc: "För en sund nervfunktion och optimal energi – säkerställ dina B12-nivåer.", points: ["Stödjer nervsystemets hälsa", "Nödvändig för blodbildning", "Förbättrar energimetabolismen", "Förebygger megaloblastisk anemi"], href: "https://goteborgskliniken.se/halsokontroller/b12-kobalamin/" },
            { name: "B9 (Folsyra, folat)", price: "", desc: "För en sund cellfunktion och en hälsosam graviditet – säkerställ dina folsyrenivåer.", points: ["Stödjer DNA-syntes och reparation", "Viktig för friska röda blodkroppar", "Stödjer fosterutveckling under graviditet", "Förebygger neuralrörsdefekter"], href: "https://goteborgskliniken.se/halsokontroller/b9-folsyra-folat/" },
          ],
        },
        {
          title: "Övriga",
          items: [
            { name: "NT-proBNP", price: "", desc: "För en tidig upptäckt och effektiv övervakning av hjärtsvikt – trygghet för ditt hjärta.", points: ["Grundläggande hjärtmarkör vid hjärtsvikt", "Indikerar hjärtbelastning", "Identifierar risk för hjärtsvikt", "Hjälper till med behandlingsplanering"], href: "https://goteborgskliniken.se/halsokontroller/nt-probnp/" },
            { name: "IGF-1", price: "", desc: "För optimal tillväxt och regenerering – övervaka dina IGF-1-nivåer.", points: ["Stimulerar celltillväxt och utveckling", "Viktig för vuxnas muskelunderhåll", "Påverkar muskelmassa och fettfördelning", "Indikerar hormonella balanser"], href: "https://goteborgskliniken.se/halsokontroller/igf-1/" },
            { name: "B-Peth - Alkoholtest", price: "", desc: "För noggrann övervakning av alkoholintag – säkerställ dina B-Peth-nivåer.", points: ["Spårar långvarig alkoholkonsumtion", "Hög specificitet för alkoholintag", "Kan detektera även måttlig konsumtion", "Användbar i kliniska och rättsliga sammanhang"], href: "https://goteborgskliniken.se/halsokontroller/b-peth-alkoholtest/" },
          ],
        },
      ],
      howTitle: "Så fungerar det",
      howSubtitle: "Göteborgskliniken erbjuder en smidig process för dina hälsokontroller",
      howSteps: [
        { title: "Boka din tid", text: "Boka din tid snabbt och enkelt online via Göteborgsklinikens webbplats." },
        { title: "Provtagning", text: "Kom till kliniken för en smidig provtagning, allt utförs av professionell personal." },
        { title: "Analys av resultat", text: "Läkare analyserar dina provsvar noggrant och skickar dig resultaten digitalt." },
        { title: "Uppföljning", text: "Få möjlighet att boka en personlig uppföljning och vidare rådgivning från läkare." },
      ],
      ctaTitle: "Redo att boka din hälsokontroll?",
      ctaText: "Boka din hälsokontroll direkt via Göteborgsklinikens webbplats.",
      ctaPrimary: "Boka hälsokontroll",
      ctaSecondary: "Kontakta Göteborgskliniken",
    },
    howItWorks: {
      eyebrow: "Så fungerar det",
      title: "Ett tydligt flöde från start till intyg",
      subtitle: "Digitalt där det fungerar, på plats där undersökningen kräver det.",
      steps: [
        {
          title: "1. Hitta rätt läkarintyg",
          text: "Svara på några enkla frågor så visar vi rätt tjänst och nästa steg.",
        },
        {
          title: "2. Identifiera dig säkert",
          text: "Besvara hälsodeklarationen med BankID. Uppgifterna blir journal.",
        },
        {
          title: "3. Träffa läkaren",
          text: "Besöket sker digitalt eller på mottagning beroende på intyget.",
        },
        {
          title: "4. Lämna prover vid behov",
          text: "Provtagning sker fysiskt via anvisad provtagningsplats.",
        },
        {
          title: "5. Intyg och uppföljning",
          text: "Läkaren granskar hela underlaget och färdigställer intyget. Vi skickar en elektronisk kopia till dig.",
        },
      ],
      secureTitle: "Journalsystemet håller ihop vårdkontakten",
      secureText:
        "Hälsodeklaration, dokument, meddelanden och läkarens bedömning hanteras i journalsystemet. Webbplatsen används bara för information och för att föra dig till rätt säkra flöde.",
      ctaPrimary: "Hitta rätt läkarintyg",
      ctaSecondary: "Inför besöket",
      metaTitle: "Så fungerar det | Intygsläkaren",
      metaDescription:
        "Så fungerar läkarintyg hos Intygsläkaren — från rätt intyg och BankID till läkarbesök, prover och färdigt intyg.",
    },
    confirmation: {
      eyebrow: "Bekräftelse",
      title: "Din tid är nu bokad!",
      subtitle: "Du kommer att få SMS och mail bekräftelse.",
      smsTitle: "SMS",
      smsText: "Du får en SMS-bekräftelse med tid och plats inom kort.",
      emailTitle: "E-post",
      emailText: "En e-postbekräftelse skickas till adressen du angav vid bokningen.",
      formTitle: "Fyll i formuläret inför besöket",
      formText: "För att vi ska kunna förbereda ditt besök behöver du fylla i formuläret nedan.",
      formCta: "Öppna formuläret i ny flik",
      seeYou: "Vi ses!",
      home: "Tillbaka till startsidan",
      allCerts: "Se alla läkarintyg",
    },
  },
  en: {
    nav: {
      topBanner: "Clinic in Gothenburg · Digital across Sweden",
      certificates: "Certificates",
      checkups: "Health checks",
      drugTesting: "Drug testing",
      faq: "FAQ",
      visit: "Before your visit",
      contact: "Contact",
      private: "Private",
      company: "Companies",
      book: "Book time",
      searchOpen: "Search certificates",
      searchClose: "Close search",
      searchPlaceholder: "Search certificates, e.g. driving or diving…",
      searchNoResults: "No certificates matched your search.",
    },
    hero: {
      eyebrow: "Medical certificates for individuals & companies",
      title: "Medical certificates — without the hassle.",
      titlePrefix: "Medical certificates for",
      subtitle:
        "Intygsläkaren is a clinic at Järntorget in Gothenburg — and a digital clinic for individuals and companies across Sweden. We help with medical certificates and assessments: from driving licences and seafaring to travel, insurance and occupational health. Book online, see clear pricing from the start, and meet experienced doctors who take you seriously.",
      bookCta: "Book an appointment",
      browseCta: "See all certificates",
      searchPlaceholder: "Search certificates, e.g. driving or diving…",
      searchAriaLabel: "Search medical certificates",
      searchCta: "Search",
      searchNoResults: "No certificates matched your search.",
      popularLabel: "Most requested",
      googleReviewsLabel: "Based on Google reviews",
      stat1: "30+",
      stat1Label: "certificate types",
      stat2: "4.9/5",
      stat2Label: "Customer Ratings",
      stat3: "Same-week",
      stat3Label: "availability",
      metaTitle: "Intygsläkaren – Digital medical certificates, fast and professional",
      metaDescription:
        "Intygsläkaren issues digital medical certificates for driving licences, seafarers, work, travel, insurance and more. Book online – fast, simple and professional.",
    },
    trust: {
      title: "Medical certificates across many areas",
      subtitle: "We offer medical certificates across many areas — digitally or at our clinic in Gothenburg.",
      areas: {
        korkort: "Higher licence categories, diabetes, ADHD, heart conditions and other driving assessments.",
        alkohol: "Alcohol interlocks, alcohol and drug certificates, and private drug testing.",
        sjofolk: "Seafarer certificates, offshore and ship health checks.",
        arbete: "Au pair, first-day sickness, studies abroad and night work.",
        resor: "Fit-to-fly, travel certificates and pregnancy travel forms.",
        forsakring: "Insurance and disability certificates.",
        sport: "Skydiving, competition and other sports certificates.",
        adoption: "Health certificates for adoption.",
        afs: "Workplace medical examinations under AFS.",
        ovriga: "Other medical certificates — get in touch and we'll help.",
      },
    },
    services: {
      eyebrow: "Our services",
      title: "Find the right certificate",
      allTitle: "All our medical certificates",
      subtitle:
        "Over 30 types of medical certificates for driving, seafaring, work, travel, insurance, sport and more.",
      searchPlaceholder: "Search certificates, e.g. driving or diving…",
      searchAriaLabel: "Search medical certificates",
      noResults: "No certificates matched your search. Try another word or get in touch.",
      filterAll: "All",
      bookCta: "Book",
      readMore: "Read more",
      readMoreAbout: "Read more about {title}",
      onsite: "On site",
      digital: "Digital",
      empty: "Can't find the certificate you need? Get in touch and we'll help.",
    },
    categories: {
      korkort: "Driving licence",
      alkohol: "Alcohol & drugs",
      sjofolk: "Seafaring",
      arbete: "Work & studies",
      resor: "Travel",
      forsakring: "Insurance",
      sport: "Sport & activities",
      adoption: "Adoption",
      afs: "Workplace (AFS)",
      ovriga: "Other certificates",
    },
    startCase: {
      eyebrow: "Choose certificate",
      title: "Find the right certificate",
      subtitle: "Pick an area and certificate type — digitally or at our clinic in Gothenburg.",
      back: "All areas",
      pickArea: "What is your case about?",
      pickAreaHint: "Choose the area that fits best. You do not share medical details in this step.",
      pickCertificate: "Which certificate do you need?",
      pickCertificateHint: "Choose the option that fits best. You do not share medical details in this step.",
      forPrefix: "Certificates for",
      browseAllTitle: "Want to see the full catalogue?",
      browseAllText: "All certificates are listed category by category.",
      browseAllCta: "View all certificates",
      priceLabel: "Price",
    },
    business: {
      eyebrow: "For companies",
      title: "Occupational health that fits your pace",
      text: "We support companies, shipping firms and organisations with workplace certificates, night-work assessments, AFS examinations and offshore health checks. Tailored programmes, invoicing and long-term partnerships.",
      cta: "Connect your company to us",
    },
    reviews: {
      eyebrow: "Patient reviews",
      title: "What our patients say",
      subtitle: "Compiled from verified Google reviews.",
    },
    visit: {
      eyebrow: "Before your visit",
      title: "How your visit works",
      steps: [
        { title: "1. Book", text: "Choose your certificate and book online — many can be completed digitally." },
        { title: "2. Meet the doctor", text: "You'll be seen on time. We go through the certificate and any tests." },
        {
          title: "3. Receive your certificate",
          text: "Most certificates are issued immediately or sent within 1–2 working days.",
        },
      ],
    },
    cta: {
      title: "Ready to book?",
      subtitle: "Slots available this week. Book online in under a minute.",
      primary: "Book time",
      secondary: "Call +46 31-761 77 00",
    },
    footer: {
      tagline: "Medical certificates & health assessments in central Gothenburg for individuals and companies, on site and digital.",
      navTitle: "Navigation",
      contactTitle: "Contact",
      legalTitle: "Information",
      address: "Järntorgsgatan 8, 413 30 Gothenburg, Sweden",
      cookies: "Cookie policy",
      privacy: "Privacy policy",
      terms: "Terms of use",
      manageCookies: "Manage cookies",
      rights: "All rights reserved.",
      createdBy: "Website created by",
    },
    cookies: {
      text: "We use cookies to improve your experience. You can choose which cookies to accept.",
      policyLink: "Read the cookie policy",
      accept: "Accept",
      decline: "Decline",
      settings: "Settings",
      modalTitle: "Cookie settings",
      modalDescription:
        "Choose which types of cookies you want to accept. Necessary cookies cannot be disabled because they are required for the website to function.",
      necessaryTitle: "Necessary cookies",
      necessaryText: "These cookies are required for the website to function properly.",
      analyticsTitle: "Analytics cookies",
      analyticsText:
        "Enables Google Analytics 4 via Google Tag Manager so we can understand how the website is used.",
      marketingTitle: "Marketing cookies",
      marketingText:
        "Enables advertising and conversion pixels (e.g. Meta Pixel) via Google Tag Manager for campaign measurement and relevant ads.",
      acceptAll: "Accept all",
      saveSettings: "Save settings",
    },
    contact: {
      eyebrow: "Contact",
      title: "Contact us",
      subtitle: "We help you with fast and reliable medical certificates. Reach out to book or ask a question.",
      bookCta: "Book time",
      callCta: "Call us",
      directionsCta: "Get directions",
      formTitle: "Send us a message",
      formSubtitle: "Have a question or want to book? Fill in the form and we'll get back to you as soon as possible.",
      name: "Name",
      email: "Email",
      company: "Company name",
      phone: "Phone number",
      message: "Message",
      attachment: "Attach files (max 3 files)",
      attachmentHint: "Allowed files: PNG, JPG/JPEG, WEBP, or PDF. Max 3 files.",
      attachmentInvalid: "Invalid file type. Please upload PNG, JPG/JPEG, WEBP, or PDF.",
      attachmentTooMany: "You can upload a maximum of 3 files.",
      consent: "I consent to my personal data being processed according to the privacy policy.",
      send: "Send message",
      sent: "Thanks! We'll get back to you shortly.",
      infoTitle: "Contact information",
      callDirect: "Call directly",
      sendEmail: "Send email",
      visitUs: "Visit us",
      hours: "Opening hours",
      hoursWeek: "Monday–Friday: 08:00–17:00",
      hoursSat: "Saturday: Closed",
      hoursSun: "Sunday: Closed",
      address: "Järntorgsgatan 8, 413 30 Gothenburg, Sweden",
      mapTitle: "Find us",
      mapSubtitle: "We're centrally located in Gothenburg with easy access by public transport.",
      faqTitle: "Frequently asked questions",
      faqTimeQ: "How long does it take to get a certificate?",
      faqTimeA: "Most certificates can be issued the same day or within 48 hours after the examination.",
      faqBookingQ: "Do I need to book an appointment in advance?",
      faqBookingA: "Yes, we recommend booking in advance to make sure we can help you at a time that suits you.",
      faqPaymentQ: "Which payment methods do you accept?",
      faqPaymentA: "We accept card payments, Klarna, and Swish.",
    },
    booking: {
      eyebrow: "Book an appointment",
      title: "Book an appointment for a medical certificate",
      subtitle:
        "Book an appointment for a medical certificate or consultation. Choose a date and time that suits you best.",
      callCta: "Call us",
      contactCta: "Contact us",
      sectionTitle: "Book your appointment online",
      sectionSubtitle:
        "Pick a date and time that suits you best. Book any of our services directly online or call us for personal assistance.",
      helpTitle: "Need help with your booking?",
      helpText: "Have questions about your booking or need help? Get in touch with us.",
      iframeTitle: "Book an appointment - Intygsläkaren",
    },
    faq: {
      eyebrow: "FAQ",
      title: "Frequently asked questions",
      subtitle:
        "Find answers to common questions about medical certificates, booking, costs and procedures.",
      bookCta: "Book an appointment",
      contactCta: "Contact us",
      stillTitle: "Still have questions?",
      stillText:
        "Don't hesitate to contact us by phone or email — we're happy to help!",
      sections: [
        {
          title: "General questions",
          items: [
            {
              q: "What is Intygsläkaren?",
              a: "Intygsläkaren is a specialist clinic in central Gothenburg that issues medical certificates and performs medical assessments for individuals and companies. We offer over 30 types of certificates with fast appointments and transparent pricing.",
            },
            {
              q: "How do I book an appointment with Intygsläkaren?",
              a: "The easiest way is to book online via our booking page. You can also call us at +46 31-761 77 00 or email info@intygslakaren.se and we'll help you.",
            },
            {
              q: "What should I bring to my visit?",
              a: "Always bring valid ID. Depending on the certificate, we may need previous medical records, glasses/contact lenses, any forms from your employer or authority, and information about medications you take.",
            },
          ],
        },
        {
          title: "Specific certificates",
          items: [
            {
              q: "What is a driving licence certificate and why do I need it?",
              a: "A driving licence certificate is a medical certificate required by the Swedish Transport Agency for certain licence categories (e.g. heavy truck, bus, taxi) or after illness. We issue all driving licence certificates and submit them digitally.",
            },
            {
              q: "Do I need a medical certificate to study or work abroad?",
              a: "Many universities, employers and visa programs require certificates of health, vaccinations or specific examinations. Send us the requirements and we'll help you obtain the right certificate in Swedish or English.",
            },
            {
              q: "What does a parachuting certificate involve?",
              a: "A parachuting certificate is a health assessment according to the Swedish Parachute Federation's requirements. We review cardiovascular health, vision, hearing and general health, and issue the certificate after the visit.",
            },
          ],
        },
        {
          title: "Procedures and costs",
          items: [
            {
              q: "How does a medical examination at Intygsläkaren work?",
              a: "You'll be seen on time. The doctor reviews your health history, performs the necessary examinations (e.g. vision, hearing, blood pressure) and fills out the certificate. Most certificates are issued during the visit.",
            },
            {
              q: "How much does a medical certificate cost?",
              a: "We have fixed, transparent prices per certificate type. Current prices are listed on each certificate page — no hidden costs.",
            },
            {
              q: "Can I cancel my appointment?",
              a: "Yes, you can cancel free of charge up to 24 hours before your booked time. Later cancellations are charged according to the price list.",
            },
          ],
        },
        {
          title: "Other questions",
          items: [
            {
              q: "How quickly will I receive my certificate?",
              a: "Most certificates are issued during the visit or sent digitally within 1–2 working days. Some certificates that require lab results may take a little longer.",
            },
            {
              q: "Can you help fill in specific forms from authorities or organisations?",
              a: "Yes, we complete forms from the Transport Agency, Social Insurance Agency, insurance companies, employers, sports federations and foreign authorities. Bring the form to your visit.",
            },
          ],
        },
      ],
    },
    visitPage: {
      eyebrow: "Before your visit",
      title: "About your visit",
      subtitle:
        "Book a video appointment or visit our clinic in Gothenburg. We offer flexible options for all types of medical certificates.",
      bookCta: "Book an appointment",
      contactCta: "Contact us",
      optionsTitle: "Choose your visit option",
      optionsSubtitle:
        "We offer both video appointments and in-person visits at our clinic. Some certificates require an in-person visit while others can be completed online.",
      videoTitle: "Video appointment",
      videoText:
        "Book a video appointment for a fast and convenient medical certificate from home or the office.",
      videoBullets: [
        "Convenient from home or the office",
        "Quick booking and turnaround",
        "Same professional service",
        "Certificate delivered digitally",
      ],
      clinicTitle: "Clinic visit",
      clinicText:
        "Visit our clinic in central Gothenburg for a complete medical examination and certificate issuance.",
      clinicBullets: [
        "Complete medical examination",
        "Vision and hearing tests",
        "Blood tests and physical assessments",
        "Personal in-person service",
      ],
      clinicAddress: "Järntorgsgatan 8, Gothenburg",
      bringTitle: "What should I bring?",
      bringItems: [
        "Valid ID (ID card or passport)",
        "Glasses or contact lenses if you use them",
        "Specific certificate requirements from the requesting organisation",
        "List of medications if you take any",
        "For video appointments: a stable internet connection and a working camera",
      ],
      duringTitle: "What happens during the visit?",
      durationTitle: "Duration",
      durationText:
        "A typical visit takes about 30–60 minutes depending on the type of certificate and examination.",
      examTitle: "Examination",
      examText:
        "The doctor performs a medical assessment and the tests required for your specific certificate.",
      issueTitle: "Certificate issuance",
      issueText:
        "If you meet the requirements, the certificate is issued immediately after the examination. You can take it with you or receive it digitally.",
      readyTitle: "Ready to book your visit?",
      readyText: "Choose between a video appointment or a clinic visit and book your time today.",
    },
    companies: {
      eyebrow: "Intygsläkaren for companies",
      title: "Medical certificates and testing for your employees.",
      subtitle:
        "Book a medical certificate or occupational health check for one employee. Need help with several people, drug testing or a recurring setup? Send a company inquiry and we will plan it together.",
      bookCta: "Book for an employee",
      inquiryCta: "Send a company inquiry",
      heroImageAlt: "Doctor in a meeting with corporate clients",
      heroCaption: "Clinic in Gothenburg",
      heroCaptionStrong: "Personal physician contact for your company",
      entriesAria: "Choose a company service",
      entries: [
        {
          title: "Occupational health checks",
          text: "Hand-intensive work, vibrations, night work and other workplace health examinations.",
          linkLabel: "Occupational health",
          hrefKey: "arbetsmiljo" as const,
        },
        {
          title: "Drug testing for companies",
          text: "Preventive, at hiring and with cause. All drug tests are reviewed by an MRO physician.",
          linkLabel: "Drug testing",
          hrefKey: "drogtestning" as const,
        },
        {
          title: "All our certificates",
          text: "The same services gathered for employers. Choose a certificate or ask us to plan several together.",
          linkLabel: "All services",
          hrefKey: "tjanster" as const,
        },
      ],
      assurance: [
        {
          title: "All drug tests are reviewed by an MRO physician",
          text: "Medical review of the test result.",
        },
        {
          title: "Laboratory analysis via Unilabs",
          text: "Our partner for drug testing samples.",
        },
      ],
      processEyebrow: "From inquiry to delivery",
      processTitle: "A shared plan for your company",
      steps: [
        {
          number: "01",
          title: "Tell us what you need",
          text: "Choose a service and tell us how many people, workplace and preferred period.",
        },
        {
          number: "02",
          title: "We plan together",
          text: "We go through documentation, visits, sampling and pricing before you confirm the setup.",
        },
        {
          number: "03",
          title: "Visits and follow-up",
          text: "Employees receive instructions. Medical information stays within healthcare, and follow-up is planned with you.",
        },
      ],
      ctaEyebrow: "Contact for employers",
      ctaTitle: "We plan around your needs",
      ctaText:
        "Tell us which services you need, for how many people and when. We will get back with a setup, pricing and times.",
      formTitle: "Company inquiry",
      formSubtitle:
        "Tell us what you need help with — certificates, occupational health checks or drug testing. We will reply by email with a setup, pricing and times.",
      formPrivacy:
        "Do not include medical details about individual employees in this form. That is handled within healthcare at the visit.",
      peopleCount: "Number of people",
      workplace: "Workplace / location",
      period: "Preferred period",
      metaTitle: "Medical certificates and drug testing for companies in Gothenburg | Intygsläkaren",
      metaDescription:
        "Medical certificates, occupational health checks and drug testing for companies in Gothenburg. MRO review, Unilabs and personal physician contact.",
    },
    about: {
      eyebrow: "About us",
      title: "About Intygsläkaren",
      subtitle:
        "We are a specialist clinic in central Gothenburg issuing medical certificates and performing medical assessments for individuals and companies.",
      introTitle: "Our clinic",
      introText1:
        "Intygsläkaren was founded with the goal of making medical certificates simple, fast and reassuring. We specialise in medical assessments and certificates and welcome patients to our clinic at Järntorget in Gothenburg, as well as digitally via video appointments.",
      introText2:
        "Our team consists of experienced specialist doctors with extensive experience in issuing certificates. We offer over 30 different types of certificates — from driving licences and seafarers to travel, insurance, sports and corporate health.",
      valuesTitle: "What we stand for",
      values: [
        { title: "Safety & quality", text: "We follow Sweden's highest standards for confidentiality and quality at every visit." },
        { title: "Clear pricing", text: "Fixed prices per certificate type — no hidden fees or surprises." },
        { title: "Fast service", text: "Available appointments the same week and many certificates issued on the spot." },
        { title: "Personal care", text: "We take our time and meet you professionally, attentively and respectfully." },
      ],
      ctaTitle: "Welcome to us",
      ctaText: "Book online or get in touch and we will help you further.",
      ctaPrimary: "Book appointment",
      ctaSecondary: "Contact us",
    },
    healthChecks: {
      eyebrow: "Health checks",
      title: "Health checks",
      subtitle:
        "Intygsläkaren partners with Göteborgskliniken for health checks. Browse all available health checks and book directly with Göteborgskliniken.",
      partnerTitle: "Our partnership",
      partnerText1:
        "Intygsläkaren partners with Göteborgskliniken to offer comprehensive health checks. Göteborgskliniken specialises in preventive medicine and carefully designed health checks that give you a tailored overview of your health.",
      partnerText2:
        "All health checks are booked and performed at Göteborgskliniken. You can book your appointment directly on their website.",
      bookAtPartner: "Book at Göteborgskliniken",
      bookCta: "Book",
      popular: "Most popular",
      sections: [
        {
          title: "Popular checks",
          items: [
            { name: "Basic Health Check", price: "SEK 995", desc: "Your health in focus – a simple and effective health check for everyone.", points: ["31 health markers", "Blood pressure, pulse, height and weight", "Cholesterol", "Detect anaemia", "Detect risk factors"], href: "https://goteborgskliniken.se/halsokontroller/basala-halsokollen/" },
            { name: "Large Health Check", price: "SEK 1695", desc: "Your health in focus – a larger and effective health check for everyone.", points: ["49 health markers", "Blood pressure, pulse, height and weight", "Hormones", "Vitamins", "PSA for men over 50"], href: "https://goteborgskliniken.se/halsokontroller/stora-halsokollen/", popular: true },
            { name: "Elite Health Check", price: "SEK 2495", desc: "Your health in focus – a complete and advanced health check for everyone.", points: ["57 health markers", "Blood pressure, pulse, height and weight", "Advanced tests", "Maximum insight", "Detect diseases early"], href: "https://goteborgskliniken.se/halsokontroller/elit-halsokollen/" },
          ],
        },
        {
          title: "Organ-specific",
          items: [
            { name: "Heart and vascular health", price: "", desc: "For a strong heart and healthy vessels – take control of your cardiovascular health.", points: ["Cholesterol profiles: HDL and LDL", "Risk ratios: HDL/LDL", "Apolipoproteins: ApoA1, ApoB", "Eight key markers"], href: "https://goteborgskliniken.se/halsokontroller/hjart-och-karlhalsan/" },
            { name: "Diabetes health", price: "", desc: "For stable blood sugar and a healthy future – take control of your diabetes health.", points: ["Pancreatic function", "Glucose", "HbA1c", "C-peptide", "Three health markers"], href: "https://goteborgskliniken.se/halsokontroller/diabeteshalsan/" },
            { name: "Prostate health", price: "", desc: "PSA screening for early detection of prostate changes.", points: ["PSA screening for early detection", "Recommended: 50–75 years", "Annual checks detect early changes"], href: "https://goteborgskliniken.se/halsokontroller/prostatahalsan/" },
            { name: "Kidney health", price: "", desc: "For healthy kidneys and a balanced body – take control of your kidney health.", points: ["Cystatin-C measurement", "Kidney function measurement", "Deep electrolyte review", "Chloride and phosphate"], href: "https://goteborgskliniken.se/halsokontroller/njurhalsan/" },
            { name: "Blood health", price: "", desc: "For a strong body and a robust immune system – take control of your blood health.", points: ["Complete blood count", "Immune system review", "Detect blood diseases"], href: "https://goteborgskliniken.se/halsokontroller/blodhalsan/" },
            { name: "Liver health", price: "", desc: "For a healthy liver and optimal health – take control of your liver function.", points: ["Review – 5 health markers", "Detect jaundice", "Detect liver diseases"], href: "https://goteborgskliniken.se/halsokontroller/leverhalsan/" },
          ],
        },
        {
          title: "Hormones",
          items: [
            { name: "Testosterone", price: "", desc: "For optimal energy, strength and well-being – monitor your testosterone levels.", points: ["Simple testosterone value", "For those who want to detect deficiency"], href: "https://goteborgskliniken.se/halsokontroller/testosteron/" },
            { name: "Advanced testosterone", price: "", desc: "For optimal energy, strength and well-being – monitor and balance your testosterone levels.", points: ["Testosterone measurement", "SHBG review", "Bioactive 'free' testosterone", "LH levels", "Complete blood count"], href: "https://goteborgskliniken.se/halsokontroller/fordjupande-testosteron/" },
            { name: "Advanced thyroid", price: "", desc: "For balanced metabolism and optimal health – monitor your thyroid function.", points: ["Thyroid review", "Antibodies – TPO and TRAK", "Free T4 and T3", "TSH measurement"], href: "https://goteborgskliniken.se/halsokontroller/fordjupande-skoldkortel/" },
            { name: "Estrogen", price: "", desc: "For hormonal balance and well-being – monitor your estrogen levels.", points: ["Effective estrogen measurement", "Insight into estrogen production", "Identifies causes of symptoms"], href: "https://goteborgskliniken.se/halsokontroller/ostrogen/" },
            { name: "Progesterone", price: "", desc: "For hormonal balance and optimal health – monitor your progesterone levels.", points: ["Measures progesterone levels", "Menstrual cycle health", "Identifies fertility-related issues"], href: "https://goteborgskliniken.se/halsokontroller/progesteron/" },
            { name: "FSH and Estrogen", price: "", desc: "For balanced hormonal health and well-being – monitor your FSH and estrogen levels.", points: ["Measures FSH and estrogen levels", "Assesses fertility and ovulation", "Provides insight into hormone balance"], href: "https://goteborgskliniken.se/halsokontroller/fsh-och-ostrogen/" },
          ],
        },
        {
          title: "Allergy",
          items: [
            { name: "Tree pollen allergy", price: "", desc: "Ease your allergy symptoms and improve your quality of life – take control of your pollen allergy.", points: ["Identifies tree pollen allergy", "Confirms specific tree allergens", "Enables tailored treatment plan", "Improves symptom management during pollen season"], href: "https://goteborgskliniken.se/halsokontroller/tradpollenallergi/" },
            { name: "Gluten intolerance test", price: "", desc: "Detect gluten intolerance and improve your digestion.", points: ["Identifies gluten intolerance", "Confirms celiac disease", "Enables tailored diet plan"], href: "https://goteborgskliniken.se/halsokontroller/glutenintoleranstest/" },
          ],
        },
        {
          title: "Vitamins",
          items: [
            { name: "B12 (Cobalamin)", price: "", desc: "For healthy nerve function and optimal energy – ensure your B12 levels.", points: ["Supports nervous system health", "Essential for blood formation", "Improves energy metabolism", "Prevents megaloblastic anaemia"], href: "https://goteborgskliniken.se/halsokontroller/b12-kobalamin/" },
            { name: "B9 (Folic acid, folate)", price: "", desc: "For healthy cell function and a healthy pregnancy – ensure your folate levels.", points: ["Supports DNA synthesis and repair", "Important for healthy red blood cells", "Supports fetal development during pregnancy", "Prevents neural tube defects"], href: "https://goteborgskliniken.se/halsokontroller/b9-folsyra-folat/" },
          ],
        },
        {
          title: "Other",
          items: [
            { name: "NT-proBNP", price: "", desc: "For early detection and effective monitoring of heart failure – peace of mind for your heart.", points: ["Key cardiac marker for heart failure", "Indicates heart strain", "Identifies risk of heart failure", "Helps with treatment planning"], href: "https://goteborgskliniken.se/halsokontroller/nt-probnp/" },
            { name: "IGF-1", price: "", desc: "For optimal growth and regeneration – monitor your IGF-1 levels.", points: ["Stimulates cell growth and development", "Important for adult muscle maintenance", "Affects muscle mass and fat distribution", "Indicates hormonal balance"], href: "https://goteborgskliniken.se/halsokontroller/igf-1/" },
            { name: "B-Peth – Alcohol test", price: "", desc: "For accurate monitoring of alcohol intake – ensure your B-Peth levels.", points: ["Tracks long-term alcohol consumption", "High specificity for alcohol intake", "Can detect even moderate consumption", "Useful in clinical and legal contexts"], href: "https://goteborgskliniken.se/halsokontroller/b-peth-alkoholtest/" },
          ],
        },
      ],
      howTitle: "How it works",
      howSubtitle: "Göteborgskliniken offers a smooth process for your health checks",
      howSteps: [
        { title: "Book your time", text: "Book your appointment quickly and easily online via Göteborgskliniken's website." },
        { title: "Sample collection", text: "Come to the clinic for a smooth sample collection, all performed by professional staff." },
        { title: "Result analysis", text: "Doctors carefully analyse your test results and send them to you digitally." },
        { title: "Follow-up", text: "Get the option to book a personal follow-up and further advice from a doctor." },
      ],
      ctaTitle: "Ready to book your health check?",
      ctaText: "Book your health check directly on Göteborgskliniken's website.",
      ctaPrimary: "Book health check",
      ctaSecondary: "Contact Göteborgskliniken",
    },
    howItWorks: {
      eyebrow: "How it works",
      title: "A clear flow from start to certificate",
      subtitle: "Digital where it works, on site when the examination requires it.",
      steps: [
        {
          title: "1. Find the right certificate",
          text: "Answer a few simple questions and we show the right service and next step.",
        },
        {
          title: "2. Identify yourself securely",
          text: "Complete the health declaration with BankID. The information becomes part of your medical record.",
        },
        {
          title: "3. Meet the doctor",
          text: "The visit takes place digitally or at the clinic depending on the certificate.",
        },
        {
          title: "4. Leave samples if needed",
          text: "Sample collection takes place in person at an assigned sampling location.",
        },
        {
          title: "5. Certificate and follow-up",
          text: "The doctor reviews the full documentation and completes the certificate. We send you an electronic copy.",
        },
      ],
      secureTitle: "The medical record system holds the care contact together",
      secureText:
        "Health declarations, documents, messages and the doctor's assessment are handled in the medical record system. The website is only used for information and to guide you into the right secure flow.",
      ctaPrimary: "Find the right certificate",
      ctaSecondary: "Before your visit",
      metaTitle: "How it works | Intygsläkaren",
      metaDescription:
        "How medical certificates work at Intygsläkaren — from finding the right certificate and BankID to the doctor visit, samples and your finished certificate.",
    },
    confirmation: {
      eyebrow: "Confirmation",
      title: "Your appointment is booked!",
      subtitle: "You will receive an SMS and email confirmation.",
      smsTitle: "SMS",
      smsText: "An SMS confirmation with the time and place will be sent shortly.",
      emailTitle: "Email",
      emailText: "An email confirmation will be sent to the address you provided when booking.",
      formTitle: "Complete the form before your visit",
      formText: "Please fill in the form below so we can prepare for your appointment.",
      formCta: "Open the form in a new tab",
      seeYou: "See you soon!",
      home: "Back to home",
      allCerts: "See all certificates",
    },
  },
} as const;

type DeepReadonly<T> = T extends (infer U)[]
  ? readonly DeepReadonly<U>[]
  : T extends object
    ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
    : T;

type Loose<T> = T extends string
  ? string
  : T extends (infer U)[]
    ? Loose<U>[]
    : T extends object
      ? { [K in keyof T]: Loose<T[K]> }
      : T;

export type Translations = Loose<typeof translations.sv>;
